/* global window */
import { Set } from 'immutable';
import { debug } from 'app/services/logger';

const parentNull      = Symbol('parentNull');
const parentUndefined = Symbol('parentUndefined');
const parentNone      = Symbol('parentNone');

const getValue = (obj, key) => (
  obj === null ? parentNull :
  obj === undefined ? parentUndefined :
  typeof obj === 'object' ? (
    typeof obj.has === 'function' && typeof obj.get === 'function'
    ? obj.has(key) ? obj.get(key) : parentNone
    : getProps(obj) ? getProps(obj)[key](obj)
    : obj[key]
  )
  : obj
);

const getKeys = obj => (
  (obj && typeof obj === 'object')
  ? typeof obj.keys === 'function'
  ? [...obj.keys()] : Object.keys(getProps(obj) || obj)
  : []
);

const mediaStreamProps = {
  id: (stream) => stream?.id,
  active: (stream) => stream?.active,
  audioTracks: (stream) => stream?.getAudioTracks?.(),
  videoTracks: (stream) => stream?.getVideoTracks?.(),
};

const mediaStreamTrackProps = {
  id: (track) => track?.id,
  enabled: (track) => track?.enabled,
  isolated: (track) => track?.isolated,
  kind: (track) => track?.kind,
  label: (track) => track?.label,
  muted: (track) => track?.muted,
  capabilities: (track) => track?.getCapabilities?.(),
  constraints: (track) => track?.getConstraints?.(),
  settings: (track) => track?.getSettings?.(),
};

const getProps = (obj) => {
  if (window.MediaStream && obj instanceof window.MediaStream) {
    return mediaStreamProps;
  }
  if (window.MediaStreamTrack && obj instanceof window.MediaStreamTrack) {
    return mediaStreamTrackProps;
  }
  return undefined;
};

export const debugTree = (name, prev, next) => {
  const keys = Set(getKeys(prev)).union(getKeys(next)).toArray();
  return keys
    .map(key => debugValue(`${name}/${key}`, getValue(prev, key), getValue(next, key)))
    .filter(x => x.length > 0)
    .reduce((left, right) => [...left, ...right], []);
};

const shouldGoDeeper = obj => obj !== null && typeof obj === 'object' && getKeys(obj).length > 0;

const debugValue = (name, prev, next) => {
  if (prev !== next) {
    return (shouldGoDeeper(prev) || shouldGoDeeper(next)
          ? debugTree(name, prev, next)
          : [{ name, prev, next }]);
  } else {
    return [];
  }
};

const formatDebugLog = log => {
  const formattedValues = log.map(({ name, prev, next }) => ({
    name,
    prev: valueString(prev),
    next: valueString(next),
  }));

  return formattedValues.map(({ name, prev, next }) => `${name}: ${prev} => ${next}`).join('\n');
};

export const printDebugTree = (name, prev, next) => {
  const log = debugTree(name, prev, next);
  if (log.length) {
    debug(formatDebugLog(log));
  }
};

const valueString = value => (
  value === parentNull ? '[NULL]' :
  value === parentUndefined ? '[UNDEFINED]' :
  value === parentNone ? '[NONE]' :
  typeof value === 'symbol' ? value.toString() :
  typeof value === 'function' ? `fn() ${value.name}` :
  typeof value === 'undefined' ? 'undefined' :
  value instanceof Error ? `${value.name || 'Error'}(${value.message})` :
  JSON.stringify(value).substring(0, 30)
);
